import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { Me } from '../api'
import { api } from '../api'
import { Link } from '../router'
import { useCart } from '../shop'
import { AuthForm } from './Auth'

type Props = { me: Me | undefined; theme: 'light' | 'dark'; setTheme: (t: 'light' | 'dark') => void; total?: number; path: string }

export function Header({ me, theme, setTheme, total, path }: Props) {
  const [login, setLogin] = useState(false)
  const qc = useQueryClient()
  const { data: cart } = useCart()

  async function logout() {
    const m = await api.logout()
    await api.csrf()
    qc.setQueryData(['me'], m)
    qc.invalidateQueries({ queryKey: ['search'] })
    qc.invalidateQueries({ queryKey: ['cart'] })
    qc.removeQueries({ queryKey: ['orders'] })
  }

  return (
    <header className="top wrap">
      <div className="top-row">
        <Link className="brand" to="/" aria-label="Bildarchiv – neue Suche">
          <img className="logo" src="/logo.png" alt="Stiftung Günter Zint" />
          <span>Bildarchiv · Recherche</span>
        </Link>
        <nav className="main" aria-label="Hauptnavigation">
          <Link to="/" aria-current={path === '/' ? 'page' : undefined}>Recherche</Link>
          <Link to="/warenkorb" aria-current={path === '/warenkorb' || path === '/kasse' ? 'page' : undefined}>
            Warenkorb{cart && cart.count > 0 && <span className="count">{cart.count}</span>}
          </Link>
          <a href="https://www.stiftung-guenter-zint.de/sammlung/" target="_blank" rel="noreferrer">Die Sammlung</a>
          <a href="https://www.stiftung-guenter-zint.de/kontakt/" target="_blank" rel="noreferrer">Kontakt</a>
        </nav>
        <div className="tools-top">
          {me?.authenticated ? (
            <div className="userbox">
              <Link to="/konto" className={path.startsWith('/konto') || path.startsWith('/bestellungen') ? 'cur' : ''}>{me.name}</Link>
              <span className={`access ${me.is_researcher ? 'researcher' : ''}`} title={me.is_researcher ? 'Sie sehen auch Bilder, die nur zur Recherche freigegeben sind.' : 'Angemeldet ohne Recherche-Zugang'}>
                {me.is_researcher ? 'Recherche-Zugang' : 'Angemeldet'}
              </span>
              {me.is_staff && <a className="linkbtn" href="/admin/">Verwaltung</a>}
              <button className="linkbtn" onClick={logout}>Abmelden</button>
            </div>
          ) : (
            <button className="linkbtn" onClick={() => setLogin(true)}>Anmelden</button>
          )}
          <div className="seg" role="group" aria-label="Farbschema">
            <button aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>Hell</button>
            <button aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>Dunkel</button>
          </div>
        </div>
      </div>
      <div className="stock">
        {total !== undefined && <span>Für Sie freigegeben: <b className="mono">{total.toLocaleString('de-DE')}</b> Objekte</span>}
        <span>Sozialdokumentarische Fotos · Musik- und Kulturfotografie · Fotografische Nachlässe</span>
      </div>
      {login && <LoginDialog onClose={() => setLogin(false)} />}
    </header>
  )
}

function LoginDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Anmelden">
        <AuthForm onDone={onClose} />
      </div>
    </>
  )
}
